import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

interface WindowData {
  window: number;
  dateRange: { start: string; end: string };
  sales: number; profit: number; orders: number; units: number;
  organicSales: number; organicOrders: number;
  ppcSales: number; ppcSpend: number; ppcOrders: number; ppcPct: number;
  sessions: number; conversion: number; refundUnits: number; cogs: number;
  acos: number; tacos: number; margin: number; roi: number; avgOrderValue: number;
  dailySales: number; dailyProfit: number; dailyOrders: number; dailyUnits: number;
}

const fmtUsd = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtUsd2 = (n: number) => `$${n.toFixed(2)}`;
const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

function cellColor(val: number, goodAbove: number, badBelow: number, invert = false): string {
  if (invert) {
    if (val <= badBelow) return 'bg-green-50 text-green-800';
    if (val >= goodAbove) return 'bg-red-50 text-red-800';
    return 'bg-yellow-50 text-yellow-800';
  }
  if (val >= goodAbove) return 'bg-green-50 text-green-800';
  if (val <= badBelow) return 'bg-red-50 text-red-800';
  return 'bg-yellow-50 text-yellow-800';
}

type MetricRow = { key: keyof WindowData; label: string; format: (v: number) => string; colorFn?: (v: number) => string; section?: string };

const METRICS: MetricRow[] = [
  { key: 'sales', label: 'Total Sales', format: fmtUsd, section: 'Revenue' },
  { key: 'dailySales', label: 'Daily Avg Sales', format: fmtUsd2 },
  { key: 'profit', label: 'Total Profit', format: fmtUsd, colorFn: (v) => cellColor(v, 1, 0) },
  { key: 'dailyProfit', label: 'Daily Avg Profit', format: fmtUsd2, colorFn: (v) => cellColor(v, 1, 0) },
  { key: 'orders', label: 'Orders', format: fmtNum, section: 'Volume' },
  { key: 'dailyOrders', label: 'Daily Avg Orders', format: (v) => v.toFixed(1) },
  { key: 'units', label: 'Units', format: fmtNum },
  { key: 'dailyUnits', label: 'Daily Avg Units', format: (v) => v.toFixed(1) },
  { key: 'organicSales', label: 'Organic Sales', format: fmtUsd, section: 'Organic vs PPC' },
  { key: 'organicOrders', label: 'Organic Orders', format: fmtNum },
  { key: 'ppcSales', label: 'PPC Sales', format: fmtUsd },
  { key: 'ppcSpend', label: 'PPC Spend', format: fmtUsd },
  { key: 'ppcOrders', label: 'PPC Orders', format: fmtNum },
  { key: 'ppcPct', label: 'PPC Sales %', format: pct },
  { key: 'sessions', label: 'Sessions', format: fmtNum, section: 'Efficiency' },
  { key: 'conversion', label: 'Conversion', format: pct, colorFn: (v) => cellColor(v, 10, 3) },
  { key: 'refundUnits', label: 'Refunds', format: fmtNum },
  { key: 'acos', label: 'ACoS', format: pct, colorFn: (v) => cellColor(v, 40, 20, true) },
  { key: 'tacos', label: 'TACoS', format: pct, colorFn: (v) => cellColor(v, 15, 8, true) },
  { key: 'margin', label: 'Margin', format: pct, colorFn: (v) => cellColor(v, 20, 5) },
  { key: 'roi', label: 'ROI', format: pct, colorFn: (v) => cellColor(v, 50, 0) },
  { key: 'avgOrderValue', label: 'Avg Order Value', format: fmtUsd2 },
];

export function SalesMovingAvg() {
  const [data, setData] = useState<WindowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showChart, setShowChart] = useState(true);

  useEffect(() => {
    api.get<{ windows: WindowData[] }>('/sales/moving-avg')
      .then((r) => setData(r.windows))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold">Moving Average Trend</h1>
        <span className="text-xs text-gray-400">All amounts in USD</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Side-by-side comparison of rolling averages. Smooths out daily noise to reveal real trends.
      </p>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {/* Visual comparison — daily avg bars */}
      {showChart && data.length > 0 && (
        <div className="mb-6 border rounded-lg bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Daily Average Comparison</h3>
            <label className="flex items-center gap-1 text-xs text-gray-500">
              <input type="checkbox" checked={showChart} onChange={(e) => setShowChart(e.target.checked)} />
              Show chart
            </label>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {data.map((w) => {
              const maxDailySales = Math.max(...data.map((d) => d.dailySales), 1);
              const barPct = (w.dailySales / maxDailySales) * 100;
              return (
                <div key={w.window} className="text-center">
                  <div className="text-xs text-gray-500 mb-1">{w.window}d</div>
                  <div className="h-24 flex items-end justify-center">
                    <div className="w-12 bg-blue-400 rounded-t transition-all" style={{ height: `${barPct}%` }} />
                  </div>
                  <div className="text-sm font-bold mt-1">{fmtUsd2(w.dailySales)}</div>
                  <div className="text-[10px] text-gray-400">{fmtUsd2(w.dailyProfit)}/day profit</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trend table */}
      {data.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[150px]">Metric</th>
                {data.map((w) => (
                  <th key={w.window} className="text-right px-4 py-2 min-w-[110px]">
                    <div className="font-bold">{w.window} Days</div>
                    <div className="text-[10px] font-normal text-gray-400">
                      {w.dateRange.start.slice(5)} – {w.dateRange.end.slice(5)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((row, i) => (
                <>
                  {row.section && (
                    <tr key={`section-${i}`} className="bg-gray-100">
                      <td colSpan={data.length + 1} className="px-3 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        {row.section}
                      </td>
                    </tr>
                  )}
                  <tr key={row.key} className="border-b hover:bg-gray-50/50">
                    <td className="px-3 py-1.5 font-medium text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                    {data.map((w) => {
                      const val = w[row.key] as number;
                      const bg = row.colorFn ? row.colorFn(val) : '';
                      return (
                        <td key={w.window} className={`px-4 py-1.5 text-right tabular-nums ${bg}`}>
                          {row.format(val)}
                        </td>
                      );
                    })}
                  </tr>
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data.length === 0 && (
        <p className="text-gray-400 text-sm mt-4">Not enough data yet. Need at least 7 days of sales history.</p>
      )}
    </div>
  );
}
